import {
  Circle, CircleCheck, Ear, Headphones, Megaphone, MoreHorizontal, PhoneForwarded, Send, Undo2,
  UsersRound,
} from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Tooltip } from "@/components/ui/misc";
import { Input, Textarea } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { CallbackPicker, useCallbackTime } from "@/features/callback-picker";
import {
  acdCall, callKindText, campaignAnswers, consultBriefing, forcedAnswer, pendingDisposition,
  repNumberText, type AcdCall, type Briefing,
} from "@/lib/acd";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { callbackWhen } from "@/lib/callbacks";
import {
  CLOSER_SEARCH_FROM, closerName, closerRows, heldChips, matchClosers, ownCallsHelp, sendHelp,
  skillChips, unreached,
} from "@/lib/handoff";
import {
  currentMonitors, endedMonitors, forgetMonitor, joiningListenIn, MONITOR_MODES, monitorAsked,
  monitorToAnswer, setMonitorMode, subscribeMonitors, type MonitorMode, type Monitoring,
} from "@/lib/monitor";
import {
  useAcdMe, useCallDispositions, useCallHold, useColleagues, useDispositionCall, useFinishConsult,
  useHandoff, useLeadCard, useMonitorMode, useMyAcdSessions, type ConsultEnding, type HandoffMode,
} from "@/lib/queries";
import type { Disposition, HandoffCloser, HandoffClosers } from "@/lib/types";
import { cn, formatPhoneNumber } from "@/lib/utils";
import { useSoftphone } from "@/softphone/softphone-context";

/**
 * The ACD's side of a call on the phone (ADR-016, ADR-017): what the rep reads before saying
 * hello, how a setter sends a caller on to a closer, and the outcome once the call ends.
 *
 * All of it reads the event-tracker's record of the rep (`GET /acd/me`), the same record the
 * availability control reads — never the phone's own idea of the call, which knows a number and
 * nothing about why it rang. `acdCall` in `lib/acd.ts` matches the two.
 */

/** The call the ACD rang this phone for, while it rings or is up; null otherwise. */
export function useAcdCall(): AcdCall | null {
  const { user } = useAuth();
  const phone = useSoftphone();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  return acdCall(me, phone.calls);
}

/** The briefing this rep is in the middle of (ADR-027), or null: their own record's word. */
export function useBriefing(): Briefing | null {
  const { user } = useAuth();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  return consultBriefing(me);
}

/** Whether the rep's campaign answers its calls for them right now (`campaignAnswers`). */
export function useCampaignAnswers(): boolean {
  const { user } = useAuth();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  return campaignAnswers(me);
}

/** While a call rings that the rep's record does not name yet: how often to read the record
 *  again, and how many times. */
const CATCH_UP_EVERY_MS = 500;
const CATCH_UP_TIMES = 10;

/**
 * The campaign's auto-answer. A call the ACD rings the rep for on a line whose campaign asks for it
 * is answered without a click, whatever this browser's own auto-answer says — which still decides
 * every other call, another campaign's included. `forcedAnswer` holds the rule.
 *
 * It is for the dialer: the ACD holds a lead who answered for their rep only for the campaign's
 * abandon window, two seconds by default, and a rep who has to reach for Answer misses most of
 * them. So the call cannot wait for the refetch hint about it. The switch's invite usually reaches
 * the browser before the event-tracker's record says the rep is ringing, and while a call rings
 * that the record does not name yet, on a rep signed in to a line that asks, the record is read
 * again every half second.
 *
 * Mounted once, in the dock, which is on every screen.
 */
export function useCampaignAutoAnswer(): void {
  const { user } = useAuth();
  const phone = useSoftphone();
  const toast = useToast();
  const { data: me, refetch } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  const target = forcedAnswer(me, phone.calls, phone.autoAnswer);
  const inForce = campaignAnswers(me) && !phone.autoAnswer;
  const ringing =
    phone.calls.find((c) => c.direction === "inbound" && c.state === "ringing")?.id ?? null;
  const answered = React.useRef(new Set<string>());

  React.useEffect(() => {
    if (!inForce || ringing === null || target !== null) return;
    let times = 0;
    void refetch();
    const timer = window.setInterval(() => {
      times += 1;
      if (times >= CATCH_UP_TIMES) window.clearInterval(timer);
      void refetch();
    }, CATCH_UP_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [inForce, ringing, target, refetch]);

  const { answer } = phone;
  React.useEffect(() => {
    if (target === null || answered.current.has(target)) return;
    answered.current.add(target);
    answer(target).catch((e: unknown) => {
      // A refused microphone is the usual cause, and retrying does not fix it: the call is left
      // ringing for the rep to take by hand, as the browser's own auto-answer leaves it.
      console.error("[softphone] campaign auto-answer failed", e);
      toast.error("Could not answer automatically", "Answer the call yourself.");
    });
  }, [target, answer, toast]);
}

/**
 * The campaign a call came through, what kind of call it is, and — for a caller handed over — who
 * sent them and their note, which is the point of a handoff: the closer does not ask the caller to
 * start again. Under the party, on the ringing screen and on the call screen.
 */
export function AcdCallStrip({ callId }: { callId: string }) {
  const acd = useAcdCall();
  const ours = acd !== null && acd.callId === callId;
  const { data: handoff } = useHandoff(ours ? acd.handoffId : null);
  // A caller for another rep's number needs that rep's name as much as a handoff needs its sender's.
  const { data: colleagues } = useColleagues({
    enabled: Boolean(handoff) || (ours && acd.meantFor !== null),
  });
  const { data: me } = useAcdMe({ enabled: ours });
  // The closer's half of a warm handoff: they have the setter on the line, not the client yet.
  const beingBriefed = ours && consultBriefing(me)?.role === "closer";
  if (!ours) return null;

  const campaign = acd.line?.campaign_name ?? acd.queue ?? "One of your lines";
  const sender = handoff
    ? (colleagues?.find((c) => c.extension === handoff.from_user)?.name ?? handoff.from_user)
    : null;
  // A caller who dialed a rep's own number (ADR-037): "Called your number", or whose it was.
  const dialed = repNumberText(
    acd.kind,
    acd.meantFor,
    me,
    (extension) => colleagues?.find((c) => c.extension === extension)?.name,
  );
  return (
    <div className="w-full rounded-lg border border-border bg-surface-sunken px-3 py-2 text-left text-xs">
      <p className="flex min-w-0 items-center gap-1.5 text-text">
        <Megaphone className="size-3.5 shrink-0 text-text-muted" aria-hidden />
        <span className="truncate font-medium">{campaign}</span>
        {acd.line?.purpose === "closer" && <span className="shrink-0 text-text-muted">· Closers</span>}
      </p>
      <p className="mt-0.5 text-text-muted">
        {dialed ?? callKindText(acd.kind)}
        {sender && ` by ${sender}`}
      </p>
      {beingBriefed && (
        <p className="mt-0.5 flex items-center gap-1.5 text-text-muted">
          <Headphones className="size-3.5 shrink-0" aria-hidden />
          {sender ? `${sender} is briefing you` : "You are being briefed"} · the caller is on hold
        </p>
      )}
      {handoff?.note && (
        <p className="mt-1.5 whitespace-pre-line border-l-2 border-accent-8 pl-2 text-text">
          {handoff.note}
        </p>
      )}
    </div>
  );
}

/** What the setter chose on the form: a closer by name, or any closer holding these skills. */
export interface CloserChoice {
  target: HandoffCloser | null;
  needs: string[];
}

/**
 * Send to a closer. The ACD takes the caller off this rep, holds them on music, and rings a closer
 * with this note on their screen; core-api picks the campaign's closer line and the `closer`
 * skill. A section of Transfer rather than a tile of its own, because handing a caller on is one
 * intention whichever way it goes.
 *
 * The setter says who (ADR-031): any closer who is free — the ACD picks, narrowed by a skill when
 * they ask, which rides the request on top of `closer` — or one by name, from the closers on that
 * line with the ACD's state of each. Only a closer who is free can be picked; the rest are shown
 * with why, so the setter can wait for Maria or choose somebody else. A named closer bypasses the
 * skills, as any pin does, so the chips only narrow the list once a name is picked. `lib/handoff.ts`
 * holds the rules; this is the form.
 */
export function SendToCloserForm({
  busy, closers, loadingClosers, onSend,
}: {
  busy: boolean;
  /** `GET /handoffs/closers`; undefined while it loads, or when it could not be read. */
  closers: HandoffClosers | undefined;
  loadingClosers: boolean;
  onSend: (note: string, mode: HandoffMode, choice: CloserChoice) => void;
}) {
  const id = React.useId();
  const [note, setNote] = React.useState("");
  const [needs, setNeeds] = React.useState<string[]>([]);
  const [search, setSearch] = React.useState("");
  const [target, setTarget] = React.useState<HandoffCloser | null>(null);
  const skills = closers?.skills ?? [];
  const chips = skillChips(skills, closers?.closers ?? []);
  const listed = closerRows(closers?.closers ?? [], needs);
  const rows = matchClosers(listed, search, skills);
  // A closer picked who has since gone from the list, or is no longer free, is not sent to by
  // surprise: the choice falls back to any closer, and their row says why. Read off the whole
  // list, never the search: typing a name that hides the pick must not throw the pick away.
  const picked =
    target !== null &&
    listed.some((r) => r.pickable && r.closer.sip_endpoint_id === target.sip_endpoint_id)
      ? target
      : null;
  React.useEffect(() => {
    if (target !== null && picked === null) setTarget(null);
  }, [target, picked]);

  const toggleNeed = (slug: string) =>
    setNeeds((now) => (now.includes(slug) ? now.filter((s) => s !== slug) : [...now, slug]));
  const choice: CloserChoice = { target: picked, needs };
  const needsText = chips.filter((c) => needs.includes(c.slug)).map((c) => c.name).join(" and ");

  return (
    <form
      className="flex flex-col gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) onSend(note, "consult", choice);
      }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p id={`${id}-who`} className="text-xs text-text-muted">
          Who takes the caller
        </p>
        {listed.length > 0 && (
          <p className="shrink-0 text-xs text-text-subtle tabular">
            {rows.length === listed.length
              ? `${listed.length} on the line`
              : `${rows.length} of ${listed.length}`}
          </p>
        )}
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Needs a skill">
          <span className="text-xs text-text-muted">Needs</span>
          {chips.map((chip) => {
            const on = needs.includes(chip.slug);
            return (
              <button
                key={chip.slug}
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() => toggleNeed(chip.slug)}
                className={cn(
                  "h-6 rounded-full border px-2 text-xs transition-colors",
                  on
                    ? "border-accent-8 bg-accent-3 text-accent-11"
                    : "border-border bg-surface text-text-muted hover:bg-surface-hover",
                )}
              >
                {chip.name}
              </button>
            );
          })}
        </div>
      )}
      {/* A line can staff dozens of closers. Past a handful the list is searched rather than
          scrolled — by name, extension or skill (`matchClosers`) — and the count above says how
          much of it a search is hiding. */}
      {listed.length > CLOSER_SEARCH_FROM && (
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Name, extension or skill"
          autoComplete="off"
          className="h-9 text-sm"
          aria-label="Find a closer"
        />
      )}
      <div
        role="radiogroup"
        aria-labelledby={`${id}-who`}
        className="flex max-h-64 flex-col gap-1 overflow-y-auto"
      >
        <ChoiceRow
          checked={picked === null}
          title="Any closer who is free"
          subtitle={
            needs.length > 0
              ? `The next free closer who holds ${needsText}`
              : "The ACD picks the next closer free"
          }
          onPick={() => setTarget(null)}
        />
        {loadingClosers ? (
          <p className="flex items-center gap-1.5 px-1 py-1 text-xs text-text-muted">
            <Spinner className="size-3.5" />
            Finding the closers…
          </p>
        ) : rows.length === 0 ? (
          <p className="px-1 py-1 text-xs text-text-muted">
            {closers === undefined
              ? "Could not read who is on the closers' line."
              : search.trim()
                ? `Nobody on the line matches “${search.trim()}”.`
                : needs.length > 0
                  ? `Nobody on the line holds ${needsText}.`
                  : "Nobody staffs the closers' line yet."}
          </p>
        ) : (
          rows.map((row) => (
            <ChoiceRow
              key={row.closer.sip_endpoint_id}
              checked={picked?.sip_endpoint_id === row.closer.sip_endpoint_id}
              disabled={!row.pickable}
              title={row.name}
              subtitle={[row.closer.user, row.status, ...heldChips(row.closer, skills)].join(" · ")}
              onPick={() => setTarget(row.closer)}
            />
          ))
        )}
      </div>
      {ownCallsHelp(rows) && <p className="px-1 text-xs text-text-muted">{ownCallsHelp(rows)}</p>}
      <label htmlFor={id} className="mt-1 text-xs text-text-muted">
        A note for the closer
      </label>
      <Textarea
        id={id}
        rows={3}
        maxLength={2000}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="What happened, what they want, what you told them"
        className="min-h-16 font-sans text-sm"
      />
      <div className="grid grid-cols-2 gap-1.5">
        <Button type="submit" className="h-10" loading={busy}>
          <Headphones />
          Warm transfer
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-10"
          disabled={busy}
          onClick={() => onSend(note, "blind", choice)}
        >
          <Send />
          Blind transfer
        </Button>
      </div>
      <p className="text-xs text-text-muted">{sendHelp(picked ? closerName(picked) : null)}</p>
    </form>
  );
}

/** One choice of who takes the caller. A radio, not a checkbox: the caller goes to one person. */
function ChoiceRow({
  checked, disabled = false, title, subtitle, onPick,
}: {
  checked: boolean;
  disabled?: boolean;
  title: string;
  subtitle: string;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={title}
      disabled={disabled}
      onClick={onPick}
      className={cn(
        "flex w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors",
        checked ? "border-accent-8 bg-accent-2" : "border-border bg-surface hover:bg-surface-hover",
        "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-surface",
      )}
    >
      {checked ? (
        <CircleCheck className="size-4 shrink-0 text-accent-11" aria-hidden />
      ) : (
        <Circle className="size-4 shrink-0 text-text-subtle" aria-hidden />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-text">{title}</span>
        <span className="block truncate text-xs text-text-muted">{subtitle}</span>
      </span>
    </button>
  );
}

/* --- the consult a setter asked for (ADR-027) ------------------------------------------------ */

/**
 * The consult this rep has just asked for, while the ACD is still looking for a closer.
 *
 * Module state rather than the dock's, because the request is made in the Transfer panel and the
 * bar that follows it lives on the call screen, which unmounts that panel. It covers the search
 * only: the moment a closer answers, the rep's own record says so and takes over. It names the
 * call as well as the request, so an ask nothing got to forget cannot be read as the next call's.
 */
let pending: { handoffId: string; callId: string } | null = null;
const watchers = new Set<() => void>();

function setPending(next: { handoffId: string; callId: string } | null): void {
  pending = next;
  for (const notify of watchers) notify();
}

export function rememberConsult(handoffId: string, callId: string): void {
  setPending({ handoffId, callId });
}

export function forgetConsult(): void {
  if (pending !== null) setPending(null);
}

/**
 * Whether a request to send this caller to a closer is already out (ADR-058): the one this rep has
 * just made, or the briefing it has turned into. One call has one live request — core-api refuses a
 * second with a 409 — so Transfer says so rather than offering to make one.
 */
export function useConsultOut(callId: string | null): boolean {
  const asked = usePendingConsult();
  const briefing = useBriefing();
  if (callId === null) return false;
  // The briefing is about the call the rep is on, which is the one Transfer is open for.
  return asked?.callId === callId || briefing?.role === "setter";
}

function usePendingConsult(): { handoffId: string; callId: string } | null {
  return React.useSyncExternalStore(
    (notify) => {
      watchers.add(notify);
      return () => {
        watchers.delete(notify);
      };
    },
    () => pending,
    () => pending,
  );
}

/** A handoff nobody is waiting on any more. */
const CONSULT_OVER = ["connected", "cancelled", "abandoned", "failed"];

/**
 * A warm handoff on the setter's call screen: the search for a closer, and then the briefing.
 *
 * What is happening comes from the rep's own record, which names the closer they are briefing. The
 * id the request answered with is only needed before that — while the ACD is still ringing closers
 * and nothing has changed for anybody.
 */
export function ConsultBar() {
  const toast = useToast();
  const asked = usePendingConsult();
  const briefing = useBriefing();
  const mine = briefing?.role === "setter" ? briefing : null;
  const handoffId = mine?.handoffId ?? asked?.handoffId ?? null;
  const { data: handoff } = useHandoff(handoffId, { watch: mine === null });
  const { data: colleagues } = useColleagues({ enabled: handoffId !== null });
  const finish = useFinishConsult();
  const [error, setError] = React.useState<string | null>(null);
  const over = handoff !== undefined && CONSULT_OVER.includes(handoff.status);
  // Who the briefing is with, else who it is for: a closer the setter named (ADR-031).
  const extension = mine?.withExtension ?? handoff?.to_user ?? handoff?.target_user ?? null;
  const closer =
    colleagues?.find((c) => c.extension === extension)?.name ?? extension ?? "a closer";
  const named = handoff?.target_user != null;
  const told = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (mine !== null || !over || handoff === undefined) return;
    forgetConsult();
    // A named closer the ACD could not reach ends the consult on its own (ADR-031): the setter,
    // still on the call, is told why, once.
    const why = unreached(handoff, closer);
    if (why && told.current !== handoff.id) {
      told.current = handoff.id;
      toast.error(why.title, why.detail);
    }
  }, [mine, over, handoff, closer, toast]);

  if (handoffId === null || (mine === null && over)) return null;
  const act = (action: ConsultEnding) => {
    setError(null);
    finish.mutate(
      { handoffId, action },
      {
        onSuccess: () => {
          forgetConsult();
          if (action === "complete") toast.success("Connected", `${closer} has the caller.`);
          else if (action === "join") {
            toast.success("Three-way call", `You, ${closer} and the caller are all on the call.`);
          } else toast.success("Handoff called off", "The caller is back with you.");
        },
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : "Could not do that just now."),
      },
    );
  };

  return (
    <div className="w-full rounded-lg border border-accent-8 bg-surface-sunken px-3 py-2 text-left text-xs">
      {mine ? (
        <>
          <p className="flex min-w-0 items-center gap-1.5 font-medium text-text">
            <Headphones className="size-3.5 shrink-0 text-text-muted" aria-hidden />
            <span className="truncate">You are briefing {closer}</span>
          </p>
          <p className="mt-0.5 text-text-muted">Your caller is on hold and cannot hear you.</p>
          <BriefingEndings
            busy={(ending) => finish.isPending && finish.variables?.action === ending}
            onEnd={act}
          />
        </>
      ) : (
        <>
          <p className="flex items-center gap-1.5 text-text">
            <Spinner className="size-3.5 shrink-0" />
            <span>
              {named
                ? `Waiting for ${closer} to pick up. You are still on the call.`
                : "Looking for a closer. You are still on the call."}
            </span>
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-2 h-9 w-full"
            loading={finish.isPending}
            onClick={() => act("cancel")}
          >
            <Undo2 />
            Never mind
          </Button>
        </>
      )}
      {error && (
        <p role="alert" className="mt-1.5 text-danger-11">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The three ways a briefing ends, drawn once for the ACD's bar above and the direct room's
 * (`DirectConsultBar`): Merge all and Take the caller back as buttons, Connect them & leave
 * behind a ⋯ menu. Until 2026-09-24 it was the filled button at the front of the row, and the
 * floor asked for it out of the way: it is the one ending that takes the rep off the call, and a
 * rep reaching for Merge all mid-briefing pressed it. One more click, meant, is the cost.
 *
 * Wrapping rather than a fixed pair of columns, because the dock is 320px and these labels are
 * words, not glyphs: side by side on the Phone page, one above the other in the dock.
 */
export function BriefingEndings({
  busy, onEnd, leave = true,
}: {
  /** Whether that ending is in flight, so its control shows it. */
  busy: (ending: ConsultEnding) => boolean;
  onEnd: (ending: ConsultEnding) => void;
  /** Whether Connect them & leave is offered at all. Not on a campaign call with an outside
   *  participant (ADR-065): the last rep leaving ends the call for the caller. */
  leave?: boolean;
}) {
  return (
    <>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Button
          type="button"
          className="h-9 min-w-40 flex-1"
          loading={busy("join")}
          onClick={() => onEnd("join")}
        >
          <UsersRound />
          Merge all
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9 min-w-40 flex-1"
          loading={busy("cancel")}
          onClick={() => onEnd("cancel")}
        >
          <Undo2 />
          Take the caller back
        </Button>
        {leave && (
          <DropdownMenu>
            <Tooltip content="More ways to end the briefing">
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-9 shrink-0"
                  aria-label="More ways to end the briefing"
                  loading={busy("complete")}
                >
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent>
              <DropdownMenuItem onSelect={() => onEnd("complete")}>
                <PhoneForwarded />
                Connect them &amp; leave
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <p className="mt-1.5 text-text-muted">
        {leave
          ? "Merge all keeps you on the call with them both. Connect them & leave, in the ⋯ menu, hands the caller over and drops you off."
          : "Merge all keeps you on the call with them both. Take the caller back drops the person you brought in."}
      </p>
    </>
  );
}

/* --- a call in its own room (ADR-029) -------------------------------------------------------- */

/** How long a hold the ACD has accepted may take to reach the rep's record before the Hold tile
 *  stops waiting for it. */
const HOLD_SETTLES_MS = 4_000;

/**
 * Hold for a call the ACD connected, which lives in a conference room: through the ACD, which plays
 * the caller their line's music in the room, because the phone's own hold would leave them
 * listening to nobody (`holdsInRoom`).
 *
 * The request is only accepted, so the tile keeps spinning until the rep's record says the caller
 * is held (or no longer is), and stops waiting after a few seconds: a hold the ACD turned down
 * leaves the record as it was.
 */
export function useRoomHold(acd: AcdCall | null): {
  held: boolean;
  pending: boolean;
  toggle: () => void;
} {
  const toast = useToast();
  const hold = useCallHold();
  const [asked, setAsked] = React.useState<boolean | null>(null);
  const held = acd?.callerHeld === true;

  React.useEffect(() => {
    if (asked === null) return;
    if (asked === held) {
      setAsked(null);
      return;
    }
    const timer = window.setTimeout(() => setAsked(null), HOLD_SETTLES_MS);
    return () => window.clearTimeout(timer);
  }, [asked, held]);

  const toggle = () => {
    if (acd === null || asked !== null) return;
    const next = !held;
    setAsked(next);
    hold.mutate(
      { callerChannelId: acd.callerChannelId, held: next },
      {
        onError: (e) => {
          setAsked(null);
          toast.error(
            next ? "Could not hold the caller" : "Could not take the caller off hold",
            e instanceof ApiError ? e.message : "Please try again.",
          );
        },
      },
    );
  };
  return { held, pending: asked !== null, toggle };
}

/* --- a supervisor listening in (ADR-029) ----------------------------------------------------- */

/** The calls this tab's supervisor is listening in on (`lib/monitor.ts`). */
export function useMonitors(): ReadonlyMap<string, Monitoring> {
  return React.useSyncExternalStore(subscribeMonitors, currentMonitors, currentMonitors);
}

/** Whether `callId` is a listen-in this phone is joining by itself (`joiningListenIn`). */
export function useJoiningListenIn(callId: string | null): boolean {
  const phone = useSoftphone();
  const monitors = useMonitors();
  const asking = React.useSyncExternalStore(subscribeMonitors, monitorAsked, monitorAsked);
  return callId !== null && joiningListenIn(phone.calls, monitors, callId, asking);
}

/**
 * A supervisor's call into a rep's call, answered without a click and whatever this browser's own
 * auto-answer says: they asked for it a moment ago on the floor (`monitorToAnswer`). The listen-in
 * they were already on is hung up first. Forgotten again once that call has come and gone
 * (`endedMonitors`). Mounted once, in the dock, beside the campaign's auto-answer.
 */
export function useMonitorAutoAnswer(): void {
  const phone = useSoftphone();
  const toast = useToast();
  const monitors = useMonitors();
  const plan = monitorToAnswer(phone.calls, monitors, phone.autoAnswer);
  const answered = React.useRef(new Set<string>());
  const seen = React.useRef(new Set<string>());
  // The plan as the effect's key: a new array each render must not re-run it.
  const target = plan?.callId ?? null;
  const ending = plan?.end.join(",") ?? "";

  const { answer, endAndAnswer, hangup, activeCallId, calls } = phone;
  React.useEffect(() => {
    if (target === null || answered.current.has(target)) return;
    answered.current.add(target);
    const earlier = ending ? ending.split(",") : [];
    void (async () => {
      // `endAndAnswer` hangs up the call the supervisor is on and waits for it to go before
      // answering; any other earlier listen-in is hung up here first.
      for (const id of earlier) if (id !== activeCallId) await hangup(id);
      await (activeCallId !== null && earlier.includes(activeCallId) ? endAndAnswer(target) : answer(target));
    })().catch((e: unknown) => {
      console.error("[softphone] monitor auto-answer failed", e);
      toast.error(
        "Could not join the call",
        "Check that this browser may use the microphone, then press Stop supervising and try again.",
      );
    });
  }, [target, ending, activeCallId, answer, endAndAnswer, hangup, toast]);

  React.useEffect(() => {
    for (const id of endedMonitors(calls, monitors, seen.current)) forgetMonitor(id);
  }, [calls, monitors]);
}

/**
 * On the supervisor's own call into a rep's call: whose call it is, who can hear them, and the three
 * ways to take part. Stop supervising, the phone's end-call button on this call, is how they leave;
 * the rep's call goes on.
 */
export function MonitorBar({ callId }: { callId: string }) {
  const monitor = useMonitors().get(callId);
  const change = useMonitorMode();
  const [error, setError] = React.useState<string | null>(null);
  if (!monitor) return null;

  const choose = (mode: MonitorMode) => {
    if (mode === monitor.mode || change.isPending) return;
    setError(null);
    change.mutate(
      { monitorId: monitor.monitorId, mode },
      {
        onSuccess: () => setMonitorMode(monitor.monitorId, mode),
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : "Could not change that just now."),
      },
    );
  };
  const current = MONITOR_MODES.find((m) => m.mode === monitor.mode) ?? MONITOR_MODES[0]!;

  return (
    <div className="w-full rounded-lg border border-accent-8 bg-surface-sunken px-3 py-2 text-left text-xs">
      <p className="flex min-w-0 items-center gap-1.5 font-medium text-text">
        <Ear className="size-3.5 shrink-0 text-text-muted" aria-hidden />
        <span className="truncate">{monitor.repName}'s call</span>
      </p>
      <p className="mt-0.5 text-text-muted" aria-live="polite">
        {current.hint}
      </p>
      <div className="mt-2 grid grid-cols-3 gap-1.5" role="group" aria-label="How you take part">
        {MONITOR_MODES.map((m) => (
          <Button
            key={m.mode}
            type="button"
            variant={m.mode === monitor.mode ? "default" : "outline"}
            className="h-9 px-2"
            aria-pressed={m.mode === monitor.mode}
            loading={change.isPending && change.variables?.mode === m.mode}
            onClick={() => choose(m.mode)}
          >
            {m.label}
          </Button>
        ))}
      </div>
      {error && (
        <p role="alert" className="mt-1.5 text-danger-11">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The line's disposition window: the call has ended, and the ACD waits for its outcome before it
 * offers the rep another (ADR-017).
 *
 * One click records an outcome — a confirmation after every call is how windows run out — and only
 * an outcome the campaign wants a note for asks for one first. A callback on a call with a lead asks
 * for its day and time (ADR-080), opening on the outcome's own wait so a hurried rep only confirms.
 * The call comes from the rep's newest queue session rather than the live record, which forgets a
 * call the moment it ends. Once an outcome is recorded it stays on screen until the ACD closes the
 * window, so a slow broker never reads as a click that did nothing.
 */
export function DispositionView() {
  const { user } = useAuth();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  const agent = me ? { user: me.user, domain: me.domain } : null;
  const sessions = useMyAcdSessions(agent);
  const session = pendingDisposition(sessions.data ?? [], agent);
  const outcomes = useCallDispositions(session?.campaign_id ?? null, session !== null);
  const record = useDispositionCall();
  // The lead's clock, for a callback's time: the card the phone already reads during the call.
  const card = useLeadCard(session?.lead_id ?? null, session?.session_id ?? null);
  const callback = useCallbackTime(card.data?.clock_timezone ?? card.data?.timezone ?? null);
  const noteId = React.useId();
  const [needsNote, setNeedsNote] = React.useState<Disposition | null>(null);
  const [scheduling, setScheduling] = React.useState<Disposition | null>(null);
  const [note, setNote] = React.useState("");
  const [recorded, setRecorded] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const choose = (outcome: Disposition, withNote?: string, callbackAt?: string) => {
    if (!session) return;
    setError(null);
    record.mutate(
      { callId: session.channel_uuid, disposition: outcome.slug, note: withNote, callbackAt },
      {
        onSuccess: (result) => {
          const when = result.callback_at
            ? callbackWhen(result.callback_at, callback.value.leadClock ? callback.value.zone : null).there
            : null;
          setRecorded(when ? `${outcome.label} for ${when}` : outcome.label);
        },
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : "Could not record the outcome."),
      },
    );
  };

  if (recorded) {
    return (
      <p role="status" className="py-2 text-center text-xs text-text-muted">
        <Spinner className="mr-1 inline size-3 align-[-2px]" />
        {recorded} is recorded. Your line opens again in a moment.
      </p>
    );
  }
  if (sessions.error) {
    return (
      <Callout tone="danger">
        Could not find the call to record:{" "}
        {sessions.error instanceof ApiError ? sessions.error.message : "please try again."}
      </Callout>
    );
  }
  if (sessions.isPending || (session !== null && outcomes.isPending)) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-4" />
      </div>
    );
  }
  if (!session) {
    return (
      <p className="py-2 text-center text-xs text-text-muted">
        Your last call's outcome is recorded. Your line opens again when its window closes.
      </p>
    );
  }

  const line = me?.lines.find((l) => l.name === session.queue_name);
  const caller =
    session.caller_name ??
    (session.caller_number ? formatPhoneNumber(session.caller_number) : "The caller");
  const offered = outcomes.data ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-text">How did the call go?</p>
        <p className="truncate text-xs text-text-muted">
          {caller} · {line?.campaign_name ?? session.queue_name}
        </p>
      </div>

      {outcomes.error ? (
        <Callout tone="danger">
          {outcomes.error instanceof ApiError
            ? outcomes.error.message
            : "Could not load the outcomes to choose from."}
        </Callout>
      ) : offered.length === 0 ? (
        <p className="text-xs text-text-muted">
          This campaign offers no outcomes to choose. An admin picks them on the campaign's
          Dispositions tab.
        </p>
      ) : (
        // As many columns as the space holds, not two: two is right in the 320px panel and
        // wastes the Phone page's stage, where the same twenty outcomes would run to ten rows.
        <div
          className="grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-1.5"
          role="group"
          aria-label="Outcomes"
        >
          {offered.map((outcome) => {
            // A callback on a call with a lead is promised for a time; an anonymous caller has no
            // record to call back, so theirs is recorded as it always was.
            const timed = outcome.kind === "callback" && session.lead_id !== null;
            const lit = needsNote?.id === outcome.id || scheduling?.id === outcome.id;
            return (
              <button
                key={outcome.id}
                type="button"
                disabled={record.isPending}
                aria-pressed={outcome.requires_note || timed ? lit : undefined}
                onClick={() => {
                  if (timed) {
                    setNeedsNote(null);
                    setScheduling(outcome);
                    callback.start(outcome);
                  } else if (outcome.requires_note) {
                    setScheduling(null);
                    setNeedsNote(outcome);
                  } else {
                    setNeedsNote(null);
                    setScheduling(null);
                    choose(outcome);
                  }
                }}
                className={cn(
                  "rounded-lg border border-border bg-surface px-2.5 py-2 text-left text-xs font-medium text-text",
                  "transition-colors hover:bg-surface-hover disabled:cursor-wait disabled:opacity-60",
                  lit && "border-accent-8 bg-accent-2 text-accent-11",
                )}
              >
                {outcome.label}
                {timed ? (
                  <span className="block text-2xs font-normal text-text-muted">
                    Pick a time{outcome.requires_note ? " · needs a note" : ""}
                  </span>
                ) : (
                  outcome.requires_note && (
                    <span className="block text-2xs font-normal text-text-muted">Needs a note</span>
                  )
                )}
              </button>
            );
          })}
        </div>
      )}

      {scheduling && (
        <form
          className="flex w-full max-w-lg flex-col gap-2"
          aria-label={`When to call back for ${scheduling.label}`}
          onSubmit={(e) => {
            e.preventDefault();
            if (!callback.value.iso || (scheduling.requires_note && !note.trim())) return;
            choose(scheduling, note.trim() || undefined, callback.value.iso);
          }}
        >
          {card.isPending ? (
            <div className="flex justify-center py-2">
              <Spinner className="size-4" />
            </div>
          ) : (
            <CallbackPicker value={callback.value} onChange={callback.set} disabled={record.isPending} />
          )}
          {scheduling.requires_note && (
            <>
              <label htmlFor={noteId} className="text-xs text-text-muted">
                A note for {scheduling.label}
              </label>
              <Textarea
                id={noteId}
                rows={3}
                maxLength={2000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="min-h-16 font-sans text-sm"
              />
            </>
          )}
          <Button
            type="submit"
            variant="default"
            className="h-10"
            loading={record.isPending}
            disabled={card.isPending || !callback.value.iso || (scheduling.requires_note && !note.trim())}
          >
            Schedule {scheduling.label}
          </Button>
        </form>
      )}

      {needsNote && (
        <form
          // The note is a paragraph, not a row of buttons: it keeps a readable measure however
          // wide the stage is.
          className="flex w-full max-w-lg flex-col gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            if (note.trim()) choose(needsNote, note);
          }}
        >
          <label htmlFor={noteId} className="text-xs text-text-muted">
            A note for {needsNote.label}
          </label>
          <Textarea
            id={noteId}
            autoFocus
            rows={3}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="min-h-16 font-sans text-sm"
          />
          <Button
            type="submit"
            variant="default"
            className="h-10"
            loading={record.isPending}
            disabled={!note.trim()}
          >
            Record {needsNote.label}
          </Button>
        </form>
      )}

      {error && <Callout tone="danger">{error}</Callout>}
    </div>
  );
}
